/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import type { PosPackageGridRow } from '../../domain/posTypes';
import { formatPosMoney } from '../../application/posMoneyFormat';

type PosPackageGridItemProps = {
  row: PosPackageGridRow;
  canEdit: boolean;
  onPress: (row: PosPackageGridRow) => void;
  addedFlash?: boolean;
};

const tileStyles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    borderRadius: 10,
    flex: 1,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    minHeight: 80,
    marginBottom: spacing.sm,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
  cardFlash: {
    borderColor: ticketColors.brandBorderStrong,
    backgroundColor: ticketColors.brandSoft,
  },
  cardHover: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderStrong,
  },
  cardPressed: {
    transform: [{ scale: 0.985 }],
  },
  body: {
    flex: 1,
    gap: 4,
  },
  name: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    lineHeight: 16,
  },
  price: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.3,
    marginTop: 'auto',
  },
  metaLine: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandBorderSoft,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
});

function PosPackageGridItemInner({
  row,
  canEdit,
  onPress,
  addedFlash = false,
}: PosPackageGridItemProps) {
  const [hovered, setHovered] = useState(false);
  const sessionsLabel = row.sessionCount === 1 ? '1 sesión' : `${row.sessionCount} sesiones`;

  return (
    <Pressable
      style={({ pressed }) => [
        tileStyles.card,
        addedFlash && tileStyles.cardFlash,
        !canEdit && { opacity: 0.55 },
        canEdit && hovered && !pressed && tileStyles.cardHover,
        canEdit && pressed && tileStyles.cardPressed,
        !canEdit && Platform.OS === 'web' ? ({ cursor: 'default' } as object) : null,
      ]}
      onPress={() => onPress(row)}
      disabled={!canEdit}
      onHoverIn={() => {
        if (canEdit) setHovered(true);
      }}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`Agregar paquete ${row.name}`}
      testID={`pos.package.add.${row.packageId}`}
    >
      <View style={tileStyles.body}>
        <View style={tileStyles.badge}>
          <Text style={tileStyles.badgeText}>Paquete</Text>
        </View>
        <Text
          style={tileStyles.name}
          numberOfLines={2}
          ellipsizeMode="tail"
          {...(Platform.OS === 'web' ? ({ title: row.name } as object) : null)}
        >
          {row.name}
        </Text>
        <Text style={tileStyles.metaLine} numberOfLines={1}>
          {sessionsLabel} · válido {row.validityDays} días
        </Text>
        <Text style={tileStyles.price} numberOfLines={1}>
          {formatPosMoney(row.price)}
        </Text>
      </View>
    </Pressable>
  );
}

export const PosPackageGridItem = React.memo(PosPackageGridItemInner);
